// @ts-nocheck

import React, { Component, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import { isWalletTypeOfBank } from 'businessLogic/Wallet/helpers';
import PlaidBankTabs from 'components/Core/Payment/Growth/PlaidExperiment/PlaidBankTabs';
import { checkPlaidExperimentEnabled } from 'components/Core/Payment/Growth/PlaidExperiment/utils/checkPlaidExperimentEnabled';
import BankForm from 'components/Core/Payment/PaymentForm/Bank/BankForm';
import PaymentItem from 'components/Core/Payment/PaymentItem/PaymentItem';
import Radio from 'components/Shared/Inputs/Radio';
import FormDraftProvider from 'contexts/FormDraftProvider';
import SegmentIO from 'reporting/SegmentIO';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { isInIXPControlTreatment } from 'store/ixp/selector';
import { getDefaultWalletFromList } from 'store/wallet/helpers';
import { REMOTE_BUTTON_ACCESS_FUNCTIONALITY } from 'store/wallet/slice';
import { colors, fontSize } from 'styles/cp';
import { Config } from 'types/Config';
import { IXP } from 'types/IXP';
import { HideModalFunc, ShowModalFunc } from 'types/Modal';
import { Payment } from 'types/Payment';
import { Wallet } from 'types/Wallet';

const splunkReporter = SplunkReporter.getInstance();
const logger = 'components/Core/Payment/Bank/Transfer/BankTransfer';

type TabType = 'instant' | 'manual';

type BankTransferProps = {
  payment: Pick<Payment, 'paymentStatus' | 'paymentMethodType'>;
  wallet: Pick<Wallet, 'userWallets' | 'selectedWalletId' | 'bankCreatePaymentTrigger'>;
  config: Pick<Config, 'endpoints'>;
  createPayment: (...args: any[]) => any;
  stopPayment: (...args: any[]) => any;
  bindFormSubmission: (...args: any[]) => any;
  changeSelectedBank: (...args: any[]) => any;
  onDeletePaymentMethodClicked: (...args: any[]) => any;
  onUpdatePaymentMethodClicked: (...args: any[]) => any;
  showModal: ShowModalFunc;
  hideModal: HideModalFunc;
  ixp?: IXP;
  plaidActiveTab?: 'instant' | 'manual';
  invoiceId?: string;
  isInNameValidationExperiment?: boolean;
  isInEnhancementsHoldoutExperiment?: boolean;
  domainId?: string;
  recipientEmail?: string;
};

type BankTransferState = {
  isAddBankHovered: boolean;
  activeTab: TabType;
  plaidFailed: boolean;
};

class BankTransfer extends Component<BankTransferProps, BankTransferState> {
  constructor(props: BankTransferProps) {
    super(props);
    this.state = {
      isAddBankHovered: false,
      activeTab: 'instant',
      plaidFailed: false,
    };
    const methodsToBind = [
      'handleAddBankMouseEnter',
      'handleAddBankMouseLeave',
      'handleTabClick',
      'handleSwitchToManual',
      'handlePlaidUnavailable',
    ];
    methodsToBind.forEach((methodName) => (this[methodName] = this[methodName].bind(this)));
  }
  componentDidMount() {
    const { changeSelectedBank, wallet, ixp, domainId, recipientEmail } = this.props;
    const { userWallets = [] } = wallet;

    if (this.props.plaidActiveTab === 'manual') {
      SegmentIO.trackPlaidEvent('MANUAL_ENTRY_VIEWED', {}, domainId, recipientEmail);
    }

    if (isInIXPControlTreatment({ ixp }, 'PlaidBankLogin')) {
      SegmentIO.plaidControl(domainId, recipientEmail);
    }

    const bankAccounts =
      userWallets && userWallets.filter((wallet) => isWalletTypeOfBank(wallet.walletType));
    if (bankAccounts && Array.isArray(bankAccounts) && bankAccounts.length > 0) {
      const defaultBank = getDefaultWalletFromList(bankAccounts);
      changeSelectedBank(defaultBank);
    } else {
      changeSelectedBank({
        id: 'AddBank',
        walletType: 'bank',
      });
    }
  }
  componentDidUpdate(prevProps: BankTransferProps, prevState: BankTransferState) {
    if (prevState.activeTab !== 'manual' && this.state.activeTab === 'manual') {
      SegmentIO.trackPlaidEvent(
        'MANUAL_ENTRY_VIEWED',
        {},
        this.props.domainId,
        this.props.recipientEmail
      );
    }
  }
  handleAddBankMouseEnter() {
    this.setState({ isAddBankHovered: true });
  }
  handleAddBankMouseLeave() {
    this.setState({ isAddBankHovered: false });
  }
  handleTabClick(tab: TabType, trigger: 'user_click' | 'error_screen_redirect' = 'user_click') {
    if (tab === this.state.activeTab) {
      return;
    }

    const previousTab = this.state.activeTab;
    this.setState({ activeTab: tab });

    splunkReporter.contextual({
      logInfo: { logLevel: 'info', logger },
      event: 'view',
      action: 'click',
      activityInfo: {
        activityObject: 'PlaidBankTabs',
        plaidAction: 'tab_switch',
        fromTab: previousTab,
        toTab: tab,
        trigger,
        domainId: this.props.domainId,
      },
    });
  }
  handleSwitchToManual() {
    this.handleTabClick('manual', 'error_screen_redirect');
  }
  handlePlaidUnavailable() {
    this.setState({ plaidFailed: true });

    splunkReporter.contextual({
      logInfo: { logLevel: 'info', logger },
      event: 'pay',
      action: 'render',
      activityInfo: {
        activityObject: 'PlaidBankTabs',
        plaidAction: 'plaid_unavailable_fallback_to_control',
        domainId: this.props.domainId,
      },
    });

    SegmentIO.plaidFailureFallbackToControl(this.props.domainId, this.props.recipientEmail);
  }
  getBankComputedProps() {
    const { isAddBankHovered } = this.state;
    const {
      wallet,
      payment,
      config,
      changeSelectedBank,
      bindFormSubmission,
      createPayment,
      stopPayment,
      onDeletePaymentMethodClicked,
      onUpdatePaymentMethodClicked,
      showModal,
      hideModal,
      isInNameValidationExperiment,
      isInEnhancementsHoldoutExperiment,
    } = this.props;
    const {
      userWallets = [],
      selectedWalletId = '',
      [REMOTE_BUTTON_ACCESS_FUNCTIONALITY.BANK_CREATE_PAYMENT_TRIGGER]: boundCreateBankFn,
    } = wallet;
    const bankAccounts =
      userWallets && userWallets.filter((wallet) => isWalletTypeOfBank(wallet.walletType));
    const eftFormAdditionalProps = {
      showModal,
      hideModal,
    };
    const bankFormProps = {
      paymentStatus: payment.paymentStatus,
      createPayment,
      stopPayment,
      boundCreateBankFn,
      bindFormSubmission,
      endpoints: config.endpoints,
      ...(payment.paymentMethodType === 'eft' && eftFormAdditionalProps),
    };
    const addBankAccount = {
      id: 'AddBank',
      walletType: 'bank',
    };
    const showBankList = bankAccounts && Array.isArray(bankAccounts) && bankAccounts.length > 0;
    const showBankForm = !showBankList || selectedWalletId === addBankAccount.id;
    const onAddBankClicked = () => {
      SegmentIO.transactionEngaged({
        activity_type: 'wallet',
        ui_action: 'enabled',
        ui_object: 'radio_button',
        ui_object_detail: 'add_bank',
        ui_access_point: 'transaction_flow',
      });
      changeSelectedBank(addBankAccount);
    };
    // @ts-ignore
    const onChangeSelectedBank = (item) => {
      SegmentIO.transactionEngaged({
        activity_type: 'wallet',
        ui_action: 'enabled',
        ui_object: 'radio_button',
        ui_object_detail: 'saved_wallet_selected',
        ui_access_point: 'transaction_flow',
      });
      changeSelectedBank(item);
    };

    return {
      isAddBankHovered,
      config,
      payment,
      changeSelectedBank,
      isInNameValidationExperiment,
      isInEnhancementsHoldoutExperiment,
      bankAccounts,
      bankFormProps,
      addBankAccount,
      showBankList,
      showBankForm,
      selectedWalletId,
      onAddBankClicked,
      onChangeSelectedBank,
      onDeletePaymentMethodClicked,
      onUpdatePaymentMethodClicked,
    };
  }
  renderSavedBanksList(computed) {
    const {
      isAddBankHovered,
      config,
      changeSelectedBank,
      bankAccounts,
      addBankAccount,
      showBankList,
      selectedWalletId,
      onAddBankClicked,
      onChangeSelectedBank,
      onDeletePaymentMethodClicked,
      onUpdatePaymentMethodClicked,
    } = computed;

    if (!showBankList) return null;

    return (
      <Fragment>
        <span className="payment-items-header">
          <FormattedMessage
            id="PAYFORM_SELECT_PAYMENT_METHOD"
            defaultMessage="Select payment method"
          />
        </span>
        <div className="payment-items-container">
          {bankAccounts.map((item) => (
            <PaymentItem
              itemType="bank"
              item={item}
              cdn={config.endpoints.cdn}
              key={item.id}
              onClick={() => onChangeSelectedBank(item)}
              onDeletePaymentMethodClicked={() => onDeletePaymentMethodClicked(item.id, 'bank')}
              onUpdatePaymentMethodClicked={() => onUpdatePaymentMethodClicked(item.id, 'bank')}
              checked={item.id === selectedWalletId}
            />
          ))}
          <div
            className="payment-item-add"
            onClick={onAddBankClicked}
            onMouseEnter={this.handleAddBankMouseEnter}
            onMouseLeave={this.handleAddBankMouseLeave}
          >
            <div
              className="payment-item-radio"
              tabIndex={0}
              onClick={() => changeSelectedBank(addBankAccount)}
              onKeyPress={() => changeSelectedBank(addBankAccount)}
              onFocus={this.handleAddBankMouseEnter}
              onBlur={this.handleAddBankMouseLeave}
            >
              <Radio
                checked={addBankAccount.id === selectedWalletId}
                value={addBankAccount.id}
                hovered={isAddBankHovered}
              />
            </div>
            <div>
              <span
                className={'add-bank' + (addBankAccount.id === selectedWalletId ? ' checked' : '')}
              >
                <FormattedMessage id="ADD_BANK_ACCOUNT" defaultMessage="Add bank account" />
              </span>
            </div>
          </div>
        </div>

        <style jsx>{`
          .payment-items-header {
            display: block;
            font-size: ${fontSize.xxs};
            color: ${colors.lightGray};
            height: 25px;
            vertical-align: middle;
            font-family: AvenirNextforINTUIT-Medium;
          }

          .payment-items-container {
            width: 100%;
            margin-bottom: 16px;
          }

          .payment-item-add {
            height: 51px;
            cursor: pointer;
            display: flex;
            border-top: 1px solid ${colors.gray05};
            align-items: center;
            .payment-item-radio {
              &:focus {
                outline: none;
              }
            }
          }

          .add-bank {
            font-size: ${fontSize.xs};
            font-style: normal;
            font-stretch: normal;
            letter-spacing: normal;
            color: ${colors.darkGray};
            margin-left: 20px;

            &.checked {
              font-family: AvenirNextforINTUIT-Medium;
            }
          }
        `}</style>
      </Fragment>
    );
  }
  renderBankForm(computed) {
    const {
      payment,
      isInNameValidationExperiment,
      isInEnhancementsHoldoutExperiment,
      bankFormProps,
    } = computed;

    return (
      <FormDraftProvider.Consumer>
        {({ updateDraft, bank, eft }) => (
          <BankForm
            isInNameValidationExperiment={isInNameValidationExperiment}
            isInEnhancementsHoldoutExperiment={isInEnhancementsHoldoutExperiment}
            bankDraft={bank || eft}
            paymentMethodType={payment.paymentMethodType}
            updateFormDraft={(draft) => updateDraft(payment.paymentMethodType, draft)}
            {...bankFormProps}
          />
        )}
      </FormDraftProvider.Consumer>
    );
  }
  render() {
    const { ixp, bindFormSubmission } = this.props;
    const { activeTab } = this.state;
    const isPlaidEnabled = ixp ? checkPlaidExperimentEnabled(ixp).isPlaidExperimentEnabled : false;
    const computed = this.getBankComputedProps();
    const { showBankForm } = computed;

    if (isPlaidEnabled && !this.state.plaidFailed) {
      const noopBind = () => {};
      const manualTabComputed =
        activeTab === 'manual'
          ? computed
          : {
              ...computed,
              bankFormProps: { ...computed.bankFormProps, bindFormSubmission: noopBind },
            };

      return (
        <Fragment>
          {this.renderSavedBanksList(computed)}
          {showBankForm && (
            <div className="plaid-bank-tabs" data-testid="plaid-bank-tabs-container">
              <div className="tab-headers" role="tablist">
                <button
                  role="tab"
                  aria-selected={activeTab === 'instant'}
                  aria-controls="instant-tab-panel"
                  id="instant-tab"
                  className={`tab-header ${activeTab === 'instant' ? 'active' : ''}`}
                  onClick={() => this.handleTabClick('instant')}
                  data-testid="plaid-instant-tab"
                >
                  <FormattedMessage
                    id="PLAID_INSTANT_BANK_LOGIN"
                    defaultMessage="Instant bank login"
                  />
                </button>
                <button
                  role="tab"
                  aria-selected={activeTab === 'manual'}
                  aria-controls="manual-tab-panel"
                  id="manual-tab"
                  className={`tab-header ${activeTab === 'manual' ? 'active' : ''}`}
                  onClick={() => this.handleTabClick('manual')}
                  data-testid="plaid-manual-tab"
                >
                  <FormattedMessage id="PLAID_MANUAL_ENTRY" defaultMessage="Manual entry" />
                </button>
              </div>

              <div className="tab-content">
                <div
                  role="tabpanel"
                  id="instant-tab-panel"
                  aria-labelledby="instant-tab"
                  className="tab-panel"
                  data-testid="plaid-instant-panel"
                  style={{ display: activeTab === 'instant' ? 'block' : 'none' }}
                >
                  <PlaidBankTabs
                    bindFormSubmission={bindFormSubmission}
                    isActive={activeTab === 'instant'}
                    onSwitchToManual={this.handleSwitchToManual}
                    onPlaidUnavailable={this.handlePlaidUnavailable}
                    domainId={this.props.domainId}
                  />
                </div>
                <div
                  role="tabpanel"
                  id="manual-tab-panel"
                  aria-labelledby="manual-tab"
                  className="tab-panel"
                  data-testid="plaid-manual-panel"
                  style={{ display: activeTab === 'manual' ? 'block' : 'none' }}
                >
                  {this.renderBankForm(manualTabComputed)}
                </div>
              </div>
            </div>
          )}

          <style jsx>{`
            .plaid-bank-tabs {
              width: 100%;
            }

            .tab-headers {
              display: flex;
              gap: 0;
              margin-bottom: 16px;
            }

            .tab-header {
              flex: 1;
              padding: 12px 12px 16px 12px;
              background: transparent;
              border: none;
              cursor: pointer;
              font-family: AvenirNextforINTUIT-Regular;
              font-size: ${fontSize.xs};
              color: ${colors.gray02};
              transition: background-color 0.2s ease;
              position: relative;
              min-height: 36px;
              border-radius: 4px;
            }

            .tab-header.active {
              color: ${colors.gray};
            }

            .tab-header:not(.active):hover::after,
            .tab-header:not(.active):focus-visible::after {
              content: '';
              position: absolute;
              bottom: 1px;
              left: 0;
              right: 0;
              height: 1px;
              background-color: ${colors.gray05};
            }

            .tab-header.active::after {
              content: '';
              position: absolute;
              bottom: 0;
              left: 0;
              right: 0;
              height: 3px;
              background-color: ${colors.green};
            }

            .tab-content {
              min-height: 200px;
            }
          `}</style>
        </Fragment>
      );
    }

    return (
      <Fragment>
        {this.renderSavedBanksList(computed)}
        {showBankForm && this.renderBankForm(computed)}
      </Fragment>
    );
  }
}
export default BankTransfer;
